"use client";

import { IListStores } from "@/utils/redux/storeLocator";
import { GoogleMap } from "@dxp-feed/google-map";
import { useEffect, useState, useRef } from "@/utils/hooks/reactUseCustomHooks";
import { grayStyle } from "@/utils/mapStyle";
import { handleRedirectOnMap } from "@/helpers/storeLocatorHelper";

const StoreDetailMapView: React.FC<{ store: IListStores | null }> = ({
  store,
}) => {
  const [marker, setMarker] = useState<google.maps.Marker | null>(null);
  const mapRef = useRef<google.maps.Map | null>(null);
  const [loading, setLoading] = useState(true);
  const markerIcon = "../stores/mapmarker.svg";

  useEffect(() => {
    if (store && mapRef.current) {
      const newMarker = generateMarker(store, mapRef.current);
      setMarker(newMarker);

      if (newMarker) {
        fitMapToMarker(mapRef.current, newMarker, 16);
      }
    }
  }, [store, mapRef.current]);

  const generateMarker = (store: IListStores, map: google.maps.Map) => {
    const lat = parseFloat(store?._geoloc?.lat || "0");
    const lng = parseFloat(store?._geoloc?.lng || "0");

    if (isNaN(lat) || isNaN(lng)) {
      console.error("Invalid geolocation data for store:", store);
      return null;
    }

    const marker = new google.maps.Marker({
      position: { lat, lng },
      title: store.locationName || "Store",
      icon: markerIcon,
      map,
    });

    // Add click event listener
    marker.addListener("click", () => {
      handleRedirectOnMap(
        store?._geoloc?.lat,
        store?._geoloc?.lng,
        store?.address,
        store?.locationName,
      );
    });

    return marker;
  };

  const fitMapToMarker = (
    map: google.maps.Map,
    marker: google.maps.Marker,
    maxZoom: number
  ) => {
    const position = marker.getPosition();
    if (position) {
      map.setCenter(position);
      map.setZoom(maxZoom);
    } else {
      console.error("Invalid marker position for map centering:", marker);
    }
  };

  const handleMapLoad = (map: google.maps.Map) => {
    mapRef.current = map;
    setLoading(false);
  };

  const hasCoordinates = !!(store?._geoloc?.lat && store?._geoloc?.lng);

  return (
    <div className="h-[300px] lgs:w-full lgs:h-full relative">
      {loading && hasCoordinates && (
        <div className="absolute inset-0 z-10 bg-gray-300 animate-pulse rounded bg-grey150" />
      )}

      {hasCoordinates ? (
        <GoogleMap
          center={{
            lat: parseFloat(store._geoloc.lat),
            lng: parseFloat(store._geoloc.lng),
          }}
          zoom={15}
          zoomControl={true}
          mapTypeControl={false}
          streetViewControl={false}
          fullscreenControl={true}
          styles={grayStyle}
          defaultIcon={markerIcon}
          onLoad={handleMapLoad}
        />
      ) : (
        <div className="h-full flex items-center justify-center text-sm text-red-500 bg-[var(--grey150)]">
          Location information is unavailable for this store.
        </div>
      )}
    </div>
  );
};

export default StoreDetailMapView;
